import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
} from "react";

import clsx from "clsx";
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import { gql } from "@apollo/client";
import { useNorthbeamQuery } from "@hooks/http";
import { type FetchIntegrationDetails } from "@north-beam/nb-client-side";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";

import { SideNavContext, type SubNavItem } from "..";
import { Error } from "../error";
import { Loading } from "../loading";
import {
  isVisible,
  scrollToSubStepElement,
} from "../onboarding-nav/scroll-utils";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { ConfirmCheckbox } from "./components/confirm-checkbox";
import { InfoPanel } from "./components/info-panel";
import { type StepMetadata } from "./configure-platforms";
import { FAQSection } from "./configure-platforms/faq";
import { PlatformSectionHeader } from "./configure-platforms/platform-section-header";
import PlatformIcon from "./configure-platforms/platforms/components/platform-icon";
import { PlatformDetailsSection } from "./configure-platforms/platforms/components/platform-sections";
import PlatformSelect from "./configure-platforms/platforms/components/platform-selector";
import { PlatformStatusIcon } from "./configure-platforms/platforms/components/platform-status-icon";
import { Platform } from "./configure-platforms/platforms/constants";
import {
  getPlatformTitle,
  sortPlatformMetadata,
  usePlatformsMetadata,
} from "./configure-platforms/platforms/metadata";
import { type PlatformState } from "./configure-platforms/platforms/platform-context";
import {
  getPlatformDetails,
  PlatformSetup,
} from "./configure-platforms/platforms/platform-setup";
import { SetupSection } from "./configure-platforms/platforms/platform-setup/components/setup-section";
import { type PlatformMetadata } from "./configure-platforms/platforms/types";
import {
  MissingStepsModal,
  StepCompletionConfirmation,
} from "./configure-platforms/step-completion-confirmation";
import { LegacyPlatformUTMImport } from "./configure-platforms/utms/utm-setup";
import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";

const FREQUENTLY_USED_PLATFORMS = [
  Platform.AMAZON_ADS,
  Platform.AMAZON_SELLER,
  Platform.ATTENTIVE,
  Platform.GOOGLE,
  Platform.KLAVIYO,
  Platform.META,
  Platform.MICROSOFT,
  Platform.PINTEREST,
  Platform.SNAPCHAT,
  Platform.TIKTOK,
  Platform.TWITTER,
  Platform.APPLOVIN,
];

const STATIC_SUB_NAV_ITEMS: SubNavItem[] = [
  {
    label: "Select the platforms to use",
    subStep: "a",
  },
];

const SideNavItem = ({
  platform,
  metadata,
  state,
}: {
  platform: Platform;
  metadata: PlatformMetadata;
  state: PlatformState;
  getState: (platform: Platform) => PlatformState | null;
}) => {
  const { isConnectionSupported, isUTMSupported } = usePlatformsMetadata();
  return (
    <div className="justify-start items-center inline-flex place-content-between w-full">
      <div className="grow shrink basis-0 self-stretch pr-1 rounded-lg justify-start items-center gap-2 flex">
        <PlatformIcon platform={platform} size={6} />
        <span className="group-hover:underline">
          {getPlatformTitle(metadata)}
        </span>
      </div>
      <div className="justify-start items-center gap-4 flex">
        <>
          <div
            className={clsx(
              "inline-flex gap-1 text-xxs font-medium tracking-wide",
              {
                "opacity-0": !isConnectionSupported(platform),
                "text-alien": state.isConnectionConfigured,
                "text-gray+2": !state.isConnectionConfigured,
              },
            )}
          >
            <PlatformStatusIcon
              status={
                state.isConnectionConfigured ? "configured" : "not-configured"
              }
            />
            INT
          </div>
          <div
            className={clsx(
              "inline-flex gap-1 text-xxs font-medium tracking-wide",
              {
                "opacity-0": !isUTMSupported(platform),
                "text-alien": state.isUTMsConfigured,
                "text-gray+2": !state.isUTMsConfigured,
              },
            )}
          >
            <PlatformStatusIcon
              status={state.isUTMsConfigured ? "configured" : "not-configured"}
            />
            UTM
          </div>
        </>
      </div>
    </div>
  );
};

export const ConfigurePlatformsStep: FC<StepChildProps> = ({
  stepMetadata: _stepMetadata,
  updateStepMetadata,
  nextStep,
  isMarkedComplete,
  toggleStepComplete,
  ...props
}) => {
  const { setSubNavItems } = useContext(SideNavContext);
  const {
    isLoadingMetadata,
    isConnectionSupported,
    isUTMSupported,
    isDisplayUTMConfirmationStep,
    platformsMissingConfiguration,
    getTitleForPlatform,
    getMetadata,
  } = usePlatformsMetadata();

  const {
    data: integrationDetailsData,
    loading: isIntegrationDetailsLoading,
    error,
  } = useNorthbeamQuery<FetchIntegrationDetails>(FETCH_INTEGRATION_DETAILS, {});
  const integrationDetails = integrationDetailsData?.integrationDetails;

  const [stepMetadata, setStepMetadata] = useState<StepMetadata>(
    _stepMetadata && "platforms" in _stepMetadata
      ? _stepMetadata
      : { platforms: [] },
  );

  const platformStateGetter = useCallback(
    (platform: Platform) => {
      const filtered = Object.entries(stepMetadata.platforms).filter(
        ([key]) => key === platform,
      );
      return filtered.length > 0 ? filtered[0][1] : null;
    },
    [stepMetadata.platforms],
  );

  useEffect(() => {
    setSubNavItems([
      ...STATIC_SUB_NAV_ITEMS,
      ...Object.entries(stepMetadata.platforms)

        .map(([platform, state]) => {
          const metadata = getMetadata(platform as Platform);
          return {
            label: () => (
              <SideNavItem
                metadata={metadata}
                platform={platform as Platform}
                state={state}
                getState={platformStateGetter}
              />
            ),
            subStep: `c_${platform}`,
          };
        })
        .sort(({ subStep: lhs }, { subStep: rhs }) => {
          return lhs.localeCompare(rhs);
        }),
    ]);
  }, [setSubNavItems, stepMetadata]);

  const [searchParams, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    noScroll: withDefault(BooleanParam, false),
  });
  useEffect(() => {
    if (searchParams.noScroll) {
      setSearchParams({ noScroll: undefined });
    }
  }, []);

  const selectPlatformsRef = useRef(null);
  const selectPlatformTitleRef = useCallback(
    (node: any) => {
      if (node === null) {
        return;
      }
      selectPlatformsRef.current = node;
      scrollToSubStepElement(node, searchParams, "a");
    },
    [searchParams],
  );

  type platformRef = [platform: string, node: HTMLDivElement];
  const platformRefs = useRef<platformRef[]>([]);
  const platformTitleRef = useCallback(
    (platform: string) => {
      return (node: any) => {
        if (node === null) {
          return;
        }

        // replace existing node if there is already a reference, otherwise push new reference
        const existingIdx = platformRefs.current.findIndex(
          ([platformRef]) => platform === platformRef,
        );
        if (existingIdx < 0) {
          platformRefs.current.push([platform, node]);
        } else {
          platformRefs.current[existingIdx][1] = node;
        }

        scrollToSubStepElement(node, searchParams, `c_${platform}`);
      };
    },
    [searchParams],
  );

  const [isMissingStepsModalOpen, setIsMissingStepsModalOpen] = useState(false);
  const remainingPlatforms = platformsMissingConfiguration(stepMetadata);

  const goToNextStep = () => {
    setSearchParams({
      step: Steps.SPEND_API,
    });
    nextStep?.();
  };
  const onClickNextStep = () => {
    if (Object.keys(remainingPlatforms).length > 0) {
      setIsMissingStepsModalOpen(true);
      return;
    } else if (!isMarkedComplete) {
      toggleStepComplete();
    }
    goToNextStep();
  };
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["configure-platforms"].index;

  return integrationDetails ? (
    <StepContainer
      isLast={false}
      title={`${stepIndex}. Connect Integrations and UTMs`}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.ORDERS_API,
        });
        props.previousStep?.();
      }}
      onMoveForwardButtonClicked={onClickNextStep}
      onScrollStopped={() => {
        if (isVisible(selectPlatformsRef)) {
          setSearchParams({
            subStep: "a",
            noScroll: true,
          });
          return;
        }

        const visiblePlatform = platformRefs.current.find(([_, node]) =>
          isVisible(node),
        );
        if (visiblePlatform) {
          setSearchParams({
            subStep: `c_${visiblePlatform[0]}`,
            noScroll: true,
          });
        }
      }}
      knowledgeBaseLink="https://docs.northbeam.io/docs/integrations-and-utms"
      knowledgeBaseText="Integrations and UTMs"
      stepCompletionConfirmation={
        <StepCompletionConfirmation
          stepMetadata={stepMetadata}
          nextStep={goToNextStep}
          toggleStepComplete={toggleStepComplete}
        />
      }
    >
      <Step
        nextStepButtonText={`${stepDictionary["spend-api"].index}. Spend API`}
        nextStep={onClickNextStep}
      >
        <MissingStepsModal
          isOpen={isMissingStepsModalOpen}
          onRequestClose={() => {
            setIsMissingStepsModalOpen(false);
          }}
          remainingPlatforms={remainingPlatforms}
          footer={
            <div className="flex items-center justify-end gap-2">
              <TertiaryButton
                onClick={() => {
                  setIsMissingStepsModalOpen(false);
                  goToNextStep();
                }}
                className="uppercase"
              >
                Skip for now
              </TertiaryButton>
              <PrimaryButton
                onClick={() => {
                  setIsMissingStepsModalOpen(false);
                }}
                className="uppercase"
              >
                Go back
              </PrimaryButton>
            </div>
          }
        />
        <div className="flex-col justify-start items-start gap-6 inline-flex font-['TT Commons']">
          <div
            className="self-stretch px-10 pt-10 pb-20 flex-col justify-start items-start flex scroll-mt-10"
            ref={selectPlatformTitleRef}
          >
            <h4 className="font-semibold">Select the platforms that you use</h4>
            <div className="w-full pt-6">
              <PlatformSelect
                isDisabled={isMarkedComplete}
                groups={{
                  "Frequently used": FREQUENTLY_USED_PLATFORMS.reduce(
                    (acc, platform) => {
                      acc[platform] = stepMetadata.platforms[platform] || {
                        isConnectionConfigured: false,
                        isUTMsConfigured: false,
                      };
                      return acc;
                    },
                    {} as { [key in Platform]?: PlatformState },
                  ),
                  // TODO display all platforms except those in the first group
                  More: getMetadata()
                    .filter(
                      (platform) =>
                        !FREQUENTLY_USED_PLATFORMS.includes(platform.name),
                    )
                    .sort(sortPlatformMetadata)
                    .reduce((acc, platform) => {
                      acc[platform.name] = stepMetadata.platforms[
                        platform.name
                      ] || {
                        isConnectionConfigured: false,
                        isUTMsConfigured: false,
                      };
                      return acc;
                    }, {} as { [key in Platform]?: PlatformState }),
                }}
                preselectedPlatforms={
                  Object.keys(stepMetadata.platforms || []) as Platform[]
                }
                onSelect={async (platform: Platform) => {
                  const updated: StepMetadata = {
                    ...stepMetadata,
                    platforms: {
                      ...stepMetadata.platforms,
                      [platform]: {},
                    },
                  };
                  setStepMetadata(updated);
                  updateStepMetadata(updated);
                }}
                onUnselect={async (platform: Platform) => {
                  const updated: StepMetadata = {
                    ...stepMetadata,
                    platforms: Object.fromEntries(
                      Object.entries(stepMetadata.platforms).filter(
                        ([key]) => key !== platform,
                      ),
                    ),
                  };
                  setStepMetadata(updated);
                  updateStepMetadata(updated);
                }}
                platformStateGetter={platformStateGetter}
              />
            </div>
            <InfoPanel label="Don't see one of your platforms or services listed here?">
              <>
                <div className="text-sm font-normal leading-tight pb-2">
                  Sometimes we're able to integrate directly with services not
                  listed above. If this isn't possible, we'll need to import
                  data through a spreadsheet.
                </div>
                <a
                  href="https://www.northbeam.io/submit-a-support-ticket"
                  target="_blank"
                  className="border border-gray-0 bg-white px-4 py-[6px] inline-flex items-center gap-2 rounded-2xl no-underline
                  hover:bg-cosmic-3 hover:border-cosmic+1"
                  rel="noreferrer"
                >
                  <i
                    className="fa-envelope fa-regular"
                    style={{ color: "#7081FF" }}
                  ></i>
                  <div className="text-black">
                    Contact us for additional guidance
                  </div>
                </a>
              </>
            </InfoPanel>
            <LegacyPlatformUTMImport
              legacyPlatforms={stepMetadata.legacyPlatforms || []}
              setLegacyPlatforms={(legacyPlatforms) => {
                const newMetadata = {
                  ...stepMetadata,
                  legacyPlatforms,
                };
                setStepMetadata(newMetadata);
                updateStepMetadata(newMetadata);
              }}
              navigateFactory={(platform: Platform) => {
                if (!Object.keys(stepMetadata.platforms).includes(platform)) {
                  return undefined;
                }
                return () => {
                  setSearchParams({
                    subStep: `c_${platform}`,
                    noScroll: false,
                  });
                };
              }}
              isDisabled={isMarkedComplete}
            />
          </div>
        </div>
        <div className="border-t pt-10 pb-20">
          <div className="px-10 flex-col justify-start items-start gap-6 inline-flex w-full">
            <h4 className="font-semibold">
              Understand how integrations and UTMs work
            </h4>
            <div className="w-full">
              <FAQSection />
            </div>
          </div>
        </div>
        <div className="flex-col justify-start items-start gap-6 inline-flex border-t">
          <div className="self-stretch px-10 py-10 flex-col justify-start items-start gap-6 flex">
            <h4 className="font-semibold">
              Connect platform integrations and configure UTMs
            </h4>
          </div>
          <div className="self-stretch flex-col justify-start items-start gap-6 flex w-full">
            {Object.entries(stepMetadata.platforms)
              .sort(([a], [b]): number => {
                return sortPlatformMetadata(
                  getMetadata(a as Platform),
                  getMetadata(b as Platform),
                );
              })
              .map(([platform, state]) => {
                const metadata = getMetadata(platform as Platform);
                const connectionSupported = isConnectionSupported(
                  platform as Platform,
                );
                const utmSupported = isUTMSupported(platform as Platform);
                const platformDetails = getPlatformDetails(
                  platform as Platform,
                );
                return (
                  <div
                    className="w-full py-10 px-10 border-t scroll-mt-10"
                    key={platform}
                    ref={platformTitleRef(platform)}
                  >
                    <div className="pb-6">
                      <PlatformSectionHeader
                        platform={platform as Platform}
                        state={state}
                        relatedStates={metadata.relatedPlatforms?.map(
                          (platform) =>
                            stepMetadata.platforms[platform] || {
                              isConnectionConfigured: false,
                              isUTMsConfigured: false,
                            },
                        )}
                      />
                    </div>
                    <div className="flex flex-col gap-4 text-sm pb-10">
                      <h1 className="text-lg font-semibold">
                        {getTitleForPlatform(platform as Platform)}{" "}
                        {connectionSupported && "integration "}
                        {connectionSupported && utmSupported && "and "}
                        {utmSupported && "UTM "}
                        details
                      </h1>
                      <div className="space-y-2">
                        <h2 className="text-sm font-semibold m-0 leading-tight">
                          Resources
                        </h2>
                        <ol className="m-0 ml-2 list-disc list-inside marker:text-xxs space-y-1">
                          <li>
                            <a
                              href={platformDetails.northbeamDocUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Northbeam’s documentation
                            </a>
                          </li>
                          {platformDetails.officialDocUrl && (
                            <li>
                              <a
                                href={platformDetails.officialDocUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {getPlatformTitle(metadata)}’s official
                                documentation
                              </a>
                            </li>
                          )}
                        </ol>
                      </div>
                      {platformDetails.additionalDetails.map(
                        (details, index) => (
                          <PlatformDetailsSection
                            key={index}
                            title={details.title}
                          >
                            {details.content}
                          </PlatformDetailsSection>
                        ),
                      )}
                    </div>
                    <div className="section-counter-start">
                      {
                        <PlatformSetup
                          platform={platform as Platform}
                          integrationDetails={integrationDetails}
                          toggleIsConfigured={(platform: Platform) =>
                            (isConfigured: boolean) => {
                              const platformState =
                                stepMetadata.platforms[platform as Platform];
                              // do not modify stepMetadata (and re-render components) if platformState did not change
                              if (
                                platformState &&
                                platformState.isConnectionConfigured ===
                                  isConfigured
                              ) {
                                return;
                              }
                              // DO NOT persist the connection configuration since the status needs to be polled, only update react state
                              setStepMetadata({
                                ...stepMetadata,
                                platforms: {
                                  ...stepMetadata.platforms,
                                  [platform]: {
                                    isUTMsConfigured:
                                      isDisplayUTMConfirmationStep(platform)
                                        ? !!platformState?.isUTMsConfigured
                                        : isConfigured,
                                    isConnectionConfigured: isConfigured,
                                  },
                                },
                              });
                            }}
                          legacyPlatforms={stepMetadata.legacyPlatforms}
                        />
                      }
                      {metadata.connectionType === "manual" && (
                        <SetupSection
                          title={`Confirm you’ve completed the platform integration for ‘${getTitleForPlatform(
                            platform as Platform,
                          )}’`}
                        >
                          <ConfirmCheckbox
                            checked={state.isConnectionConfigured}
                            disabled={isMarkedComplete}
                            id={`completedIntegration-${platform}`}
                            onChange={(isConfigured) => {
                              const updated: StepMetadata = {
                                ...stepMetadata,
                                platforms: {
                                  ...stepMetadata.platforms,
                                  [platform]: {
                                    ...stepMetadata.platforms[
                                      platform as Platform
                                    ],
                                    isConnectionConfigured: isConfigured,
                                  },
                                },
                              };
                              setStepMetadata(updated);
                              updateStepMetadata(updated);
                            }}
                            showSuccess
                          >
                            I've completed the platform integration for ‘
                            {getTitleForPlatform(platform as Platform)}‘
                          </ConfirmCheckbox>
                        </SetupSection>
                      )}
                      {isDisplayUTMConfirmationStep(platform as Platform) && (
                        <SetupSection
                          title={
                            <>
                              Confirm you’ve updated UTMs for ‘
                              {getTitleForPlatform(platform as Platform)}’
                            </>
                          }
                        >
                          <ConfirmCheckbox
                            id={`completedUTMs-${platform}`}
                            disabled={isMarkedComplete}
                            onChange={(isConfigured) => {
                              const updated: StepMetadata = {
                                ...stepMetadata,
                                platforms: {
                                  ...stepMetadata.platforms,
                                  [platform]: {
                                    isUTMsConfigured: isConfigured,
                                  },
                                },
                              };
                              setStepMetadata(updated);
                              updateStepMetadata(updated);
                            }}
                            checked={state.isUTMsConfigured}
                            showSuccess
                          >
                            {metadata.utmCompletionText?.label || (
                              <>
                                I've configured ‘
                                {getTitleForPlatform(platform as Platform)}‘
                                UTMs
                              </>
                            )}
                          </ConfirmCheckbox>
                        </SetupSection>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </Step>
    </StepContainer>
  ) : error ? (
    <Error content="There was an error loading your settings." />
  ) : isIntegrationDetailsLoading || isLoadingMetadata ? (
    <Loading />
  ) : (
    <></>
  );
};

export const FETCH_INTEGRATION_DETAILS = gql`
  query FetchIntegrationDetails {
    integrationDetails
  }
`;
